import { Event } from '../model/event';
import { MapMarker } from '../model/map-marker';
import { Organization } from '../model/organization';
import { GameTable } from '../model/game-table';
import { formatItalianDate } from '../pipes/italian-date.pipe';
import { GeoCoordinates, parseCoordinates } from './geo-coordinates';

export function countMappableEvents(events: readonly Event[]): number {
  return events.filter((event) => !!parseCoordinates(event.location)).length;
}

export function tableCoordinates(table: GameTable): GeoCoordinates | null {
  return parseCoordinates(table.location) ?? parseCoordinates(table.event?.location);
}

export function countMappableTables(tables: readonly GameTable[]): number {
  return tables.filter((table) => !!tableCoordinates(table)).length;
}

export function organizationCoordinates(
  orgId: number,
  events: readonly Event[],
  now = Date.now(),
): GeoCoordinates | null {
  const orgEvents = events.filter(
    (event) => event.organization?.id === orgId && !!parseCoordinates(event.location),
  );
  if (orgEvents.length === 0) {
    return null;
  }
  const representative = pickRepresentativeEvent(orgEvents, now);
  return parseCoordinates(representative.location);
}

export function eventMapMarkers(events: readonly Event[], locale = 'it-IT'): MapMarker[] {
  return events.flatMap((event) => {
    if (event.active === false) {
      return [];
    }
    const coords = parseCoordinates(event.location);
    if (!coords || event.id == null) {
      return [];
    }

    const when = formatItalianDate(event.startDate, 'dd MMM yyyy', locale);
    const subtitle = [event.organization?.organizationName, when].filter(Boolean).join(' · ');

    return [
      {
        id: `event-${event.id}`,
        lat: coords.lat,
        lng: coords.lng,
        title: event.name,
        subtitle: subtitle || undefined,
        kind: 'event',
        linkHref: `#/events/detail/${event.id}`,
        linkLabel: 'Apri evento',
      },
    ];
  });
}

export function organizationMapMarkers(
  events: readonly Event[],
  organizations?: readonly Organization[],
  locale = 'it-IT',
): MapMarker[] {
  const allowedIds = organizations
    ? new Set(organizations.map((organization) => organization.id))
    : undefined;
  const byOrg = new Map<number, Event[]>();

  for (const event of events) {
    if (event.active === false) {
      continue;
    }
    const orgId = event.organization?.id;
    if (orgId == null || (allowedIds && !allowedIds.has(orgId))) {
      continue;
    }
    if (!parseCoordinates(event.location)) {
      continue;
    }
    const grouped = byOrg.get(orgId) ?? [];
    grouped.push(event);
    byOrg.set(orgId, grouped);
  }

  const now = Date.now();
  const markers: MapMarker[] = [];

  for (const [orgId, orgEvents] of byOrg) {
    const representative = pickRepresentativeEvent(orgEvents, now);
    const coords = parseCoordinates(representative.location);
    if (!coords) {
      continue;
    }

    const listedName = organizations?.find((organization) => organization.id === orgId)
      ?.organizationName;
    const title = listedName || representative.organization?.organizationName || 'Organizzazione';

    markers.push({
      id: `organization-${orgId}`,
      lat: coords.lat,
      lng: coords.lng,
      title,
      subtitle: `Posizione da: ${representative.name}`,
      kind: 'organization',
      linkHref: `#/organizations/detail/${orgId}`,
      linkLabel: 'Apri organizzazione',
    });
  }

  return markers;
}

export function tableMapMarkers(tables: readonly GameTable[]): MapMarker[] {
  return tables.flatMap((table) => {
    const coords = tableCoordinates(table);
    if (!coords) {
      return [];
    }

    const eventName = table.event?.name;
    const extraLinks =
      table.event?.id != null
        ? [{ href: `#/events/detail/${table.event.id}`, label: `Evento: ${table.event.name}` }]
        : undefined;

    return [
      {
        id: `table-${table.id}`,
        lat: coords.lat,
        lng: coords.lng,
        title: table.tableName,
        subtitle: [table.playedGame?.gameName, eventName].filter(Boolean).join(' · ') || undefined,
        kind: 'table' as const,
        linkHref: `#/tables/detail/${table.id}`,
        linkLabel: 'Apri tavolo',
        extraLinks,
      },
    ];
  });
}

function pickRepresentativeEvent(events: readonly Event[], now: number): Event {
  const upcoming = events
    .filter((event) => (Date.parse(event.startDate) || 0) >= now)
    .sort((first, second) => (Date.parse(first.startDate) || 0) - (Date.parse(second.startDate) || 0));

  if (upcoming.length > 0) {
    return upcoming[0];
  }

  return [...events].sort(
    (first, second) => (Date.parse(second.startDate) || 0) - (Date.parse(first.startDate) || 0),
  )[0];
}
